import React, { useMemo, useCallback, useState } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
} from "react-leaflet";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import "leaflet/dist/leaflet.css";

import TicketDetailsModalContents from "../EventTickets/TicketDetailsModalContents";

// Continental US bounds [[south, west], [north, east]]
const US_BOUNDS = [
  [24, -125],
  [50, -66],
];
const US_BOUNDS_OBJ = {
  latMin: 24,
  latMax: 50,
  lngMin: -125,
  lngMax: -66,
};
const US_CENTER = [39, -98];
const US_ZOOM = 4;

const DEFAULT_COLORS = [
  "#1976d2",
  "#2e7d32",
  "#ed6c02",
  "#9c27b0",
  "#00838f",
  "#c62828",
];

const colorByTicketTypeKey = (ticketTypes) =>
  (ticketTypes || []).reduce((acc, tt, i) => {
    acc[tt.key] = DEFAULT_COLORS[i % DEFAULT_COLORS.length];
    return acc;
  }, {});

const safeTicketLabel = (ticket) =>
  ticket?.displayName ||
  ticket?.attendeeName ||
  ticket?.purchaserName ||
  ticket?.purchaserEmail ||
  ticket?.id ||
  "Ticket";

/**
 * Builds map points from tickets that have locationFromZipcode with lat/lng.
 * Each point is colored by ticket type. Optionally restricts to US bounds.
 */
export const getMapPointsFromTickets = (tickets, ticketTypes, usOnly = true) => {
  if (!tickets?.length) return [];
  const colorByKey = (ticketTypes || []).reduce((acc, tt, i) => {
    acc[tt.key] = DEFAULT_COLORS[i % DEFAULT_COLORS.length];
    return acc;
  }, {});
  const points = [];
  for (const ticket of tickets) {
    const loc = ticket.locationFromZipcode;
    if (!loc || typeof loc.lat !== "number" || typeof loc.lng !== "number")
      continue;
    if (usOnly) {
      if (
        loc.lat < US_BOUNDS_OBJ.latMin ||
        loc.lat > US_BOUNDS_OBJ.latMax ||
        loc.lng < US_BOUNDS_OBJ.lngMin ||
        loc.lng > US_BOUNDS_OBJ.lngMax
      )
        continue;
    }
    const ticketType = (ticketTypes || []).find(
      (tt) => tt.key === ticket.ticketTypeKey,
    );
    points.push({
      id: ticket.id,
      coordinates: [loc.lng, loc.lat],
      ticketTypeKey: ticket.ticketTypeKey,
      ticketTypeName: ticketType?.name ?? ticket.ticketTypeKey,
      color: colorByKey[ticket.ticketTypeKey] ?? "#666",
      city: loc.city,
      state: loc.state,
    });
  }
  return points;
};

export const buildTicketPoints = (tickets, ticketTypes) => {
  const raw = getMapPointsFromTickets(tickets, ticketTypes, true);
  const colorMap = colorByTicketTypeKey(ticketTypes);

  // Keep original ticket objects for labels; index by id for quick lookup.
  const ticketById = (tickets || []).reduce((acc, t) => {
    if (t?.id) acc[t.id] = t;
    return acc;
  }, {});

  return raw.map((p) => {
    const [lng, lat] = p.coordinates;
    const ticket = ticketById[p.id];
    return {
      id: p.id,
      lat,
      lng,
      city: p.city,
      state: p.state,
      ticketTypeKey: p.ticketTypeKey,
      ticketTypeName: p.ticketTypeName,
      label: safeTicketLabel(ticket),
      color: colorMap[p.ticketTypeKey] ?? p.color ?? "#666",
    };
  });
};

function ResetViewButton({ bounds }) {
  const map = useMap();
  const handleReset = useCallback(() => {
    map.flyToBounds(bounds, { padding: [20, 20], maxZoom: 10 });
  }, [map, bounds]);
  return (
    <Box
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1000,
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 1,
      }}
      data-testid="audience-map-osm-reset"
    >
      <Tooltip title="Reset view">
        <IconButton size="small" onClick={handleReset} aria-label="Reset view">
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const AudienceMapOSM = ({
  event,
  tickets,
  ticketTypes,
  title = "Audience Locations",
}) => {
  const points = useMemo(
    () => buildTicketPoints(tickets, ticketTypes),
    [tickets, ticketTypes],
  );

  const [popupState, setPopupState] = useState(null);
  // popupState: { lat: number, lng: number, city?: string, state?: string, items: Array<{id,label,ticketTypeName}> }
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [editingOrderFormKey, setEditingOrderFormKey] = useState(null);

  const handleMarkerClick = useCallback(
    (map, latlng) => {
      if (!map || !latlng) return;
      const clickPoint = map.latLngToContainerPoint(latlng);
      const thresholdPx = 10;

      const nearby = points.filter((p) => {
        const pt = map.latLngToContainerPoint([p.lat, p.lng]);
        const dx = pt.x - clickPoint.x;
        const dy = pt.y - clickPoint.y;
        return Math.sqrt(dx * dx + dy * dy) <= thresholdPx;
      });

      if (nearby.length === 0) {
        setPopupState(null);
        return;
      }

      // Prefer location text from the clicked latlng's nearest point if possible.
      const first = nearby[0];
      setPopupState({
        lat: latlng.lat,
        lng: latlng.lng,
        city: first.city,
        state: first.state,
        items: nearby
          .slice()
          .sort((a, b) => {
            const byLabel = (a.label || "").localeCompare(b.label || "");
            if (byLabel !== 0) return byLabel;
            return (a.ticketTypeName || "").localeCompare(b.ticketTypeName || "");
          })
          .map((p) => ({
            id: p.id,
            ticketTypeName: p.ticketTypeName,
            label: p.label,
          })),
      });
    },
    [points],
  );

  const selectedTicket = useMemo(() => {
    if (!selectedTicketId) return null;
    return (tickets || []).find(({ id }) => id === selectedTicketId) || null;
  }, [tickets, selectedTicketId]);

  const timezone = event?.timezone;

  return (
    <Paper elevation={2} sx={{ height: "100%", overflow: "hidden" }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <MapIcon color="action" />
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: 480,
          position: "relative",
          px: 2,
          pb: 2,
        }}
        data-testid="audience-map-osm-container"
      >
        {points.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No locations to show.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                height: "100%",
                width: "100%",
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "grey.50",
              }}
            >
              <MapContainer
                center={US_CENTER}
                zoom={US_ZOOM}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom
                data-testid="audience-map-osm-map"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {points.map((p) => (
                  <CircleMarker
                    key={p.id}
                    center={[p.lat, p.lng]}
                    pathOptions={{
                      fillColor: p.color,
                      color: "#fff",
                      weight: 1,
                      fillOpacity: 0.9,
                      radius: 6,
                    }}
                    data-testid="audience-map-osm-marker"
                    eventHandlers={{
                      click: (e) => {
                        const map = e?.target?._map;
                        handleMarkerClick(map, e?.latlng);
                      },
                    }}
                  >
                    {/* Popup is handled globally so clicks can show multiple nearby points */}
                  </CircleMarker>
                ))}
                {popupState && (
                  <Popup
                    position={[popupState.lat, popupState.lng]}
                    eventHandlers={{
                      remove: () => setPopupState(null),
                    }}
                  >
                    <strong>
                      {popupState.items.length} ticket
                      {popupState.items.length === 1 ? "" : "s"}
                    </strong>
                    {popupState.city && popupState.state && (
                      <>
                        <br />
                        {popupState.city}, {popupState.state}
                      </>
                    )}
                    <div
                      style={{
                        maxHeight: 240,
                        overflowY: "auto",
                        marginTop: 8,
                      }}
                    >
                      <ul style={{ margin: "0 0 0 16px", padding: 0 }}>
                        {popupState.items.map((t) => (
                          <li key={t.id} style={{ marginBottom: 6 }}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedTicketId(t.id);
                                setPopupState(null);
                              }}
                              data-testid={`audience-map-osm-ticket-${t.id}`}
                              style={{
                                all: "unset",
                                cursor: "pointer",
                                display: "inline",
                              }}
                            >
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontWeight: 600 }}>
                                  {t.label || "Ticket"}
                                </span>
                                <span style={{ fontSize: 12, opacity: 0.8 }}>
                                  {t.ticketTypeName}
                                </span>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Popup>
                )}
                <ResetViewButton bounds={US_BOUNDS} />
              </MapContainer>
            </Box>
          </>
        )}
      </Box>
      {points.length > 0 && ticketTypes?.length > 1 && (
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {(ticketTypes || []).slice(0, 6).map((tt, i) => (
            <Box
              key={tt.key}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor:
                    [
                      "#1976d2",
                      "#2e7d32",
                      "#ed6c02",
                      "#9c27b0",
                      "#00838f",
                      "#c62828",
                    ][i % 6],
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {tt.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {Boolean(selectedTicketId) && (
        <Box
          onClick={() => {
            setSelectedTicketId(null);
            setEditingOrderFormKey(null);
          }}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0, 0, 0, 0.35)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
          data-testid="audience-map-osm-ticket-modal"
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: { xs: "100%", sm: 500, md: 600 },
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <TicketDetailsModalContents
              event={event}
              ticket={selectedTicket}
              timezone={timezone}
              handleClose={() => {
                setSelectedTicketId(null);
                setEditingOrderFormKey(null);
              }}
              editingOrderFormKey={editingOrderFormKey}
              setEditingOrderFormKey={setEditingOrderFormKey}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default AudienceMapOSM;
